/**
 * Loading Manager — single source of truth for all loading states.
 *
 * Singleton pattern. All loading (route, query, mutation, bootstrap) flows through here.
 * LoadingManager tracks "what operations are active" — never "how should the UI look".
 * Presentation decisions belong to LoadingRoot.
 *
 * Usage:
 *   import { loadingManager } from '$lib/loading/loading-manager.js'
 *
 *   const id = loadingManager.start('mutation', { source: 'redeem-key' })
 *   loadingManager.finish(id)
 *
 *   loadingManager.subscribe(state => { console.log(state.active) })
 */

let nextId = 0

export class LoadingManager {
  /**
   * Map of active loading entries. Each entry represents one in-flight operation.
   * Reference count = map size. state.active is true when map is non-empty.
   * Entries are deleted on finish(), so the count is inherently reference-counted.
   * @type {Map<string, import('./types.js').LoadingEntry>}
   */
  #entries = new Map()

  /** @type {Array<import('./types.js').LoadingHistoryEntry>} */
  #history = []

  /** @type {Set<(state: import('./types.js').LoadingManagerState) => void>} */
  #listeners = new Set()

  /** @type {import('./types.js').LoadingManagerState} */
  #state

  /** A notification is already scheduled for this microtask. */
  #notifyQueued = false

  constructor() {
    this.#state = this.#computeState()
  }

  /**
   * Start a loading operation.
   * @param {import('./types.js').LoadingType} type
   * @param {import('./types.js').StartLoadingOptions} [options]
   * @returns {string} loadingId
   */
  start(type, options = {}) {
    const id = `load-${++nextId}-${type}`
    /** @type {import('./types.js').LoadingEntry} */
    const entry = {
      id,
      type,
      source: options.source,
      message: options.message,
      status: 'pending',
      startedAt: Date.now(),
      progress: type === 'route' ? 0 : undefined,
    }
    this.#entries.set(id, entry)
    this.#notify()
    return id
  }

  /**
   * Update progress for a loading operation (route transitions).
   * @param {string} id
   * @param {number} progress - 0 to 1
   */
  updateProgress(id, progress) {
    const entry = this.#entries.get(id)
    if (!entry) return
    entry.progress = Math.max(0, Math.min(1, progress))
    this.#notify()
  }

  /**
   * Finish a loading operation. Entry is deleted immediately.
   * @param {string} id
   * @param {{ reason?: import('./types.js').CompletionReason }} [options]
   */
  finish(id, { reason = 'success' } = {}) {
    const entry = this.#entries.get(id)
    if (!entry) return
    const finishedAt = Date.now()
    this.#history.push({
      id: entry.id,
      type: entry.type,
      source: entry.source || '',
      startedAt: entry.startedAt,
      finishedAt,
      duration: finishedAt - entry.startedAt,
      reason,
    })
    if (this.#history.length > 50) this.#history.shift()
    this.#entries.delete(id)
    this.#notify()
  }

  /** @returns {Array<import('./types.js').LoadingHistoryEntry>} */
  get history() {
    return [...this.#history]
  }

  /** @returns {import('./types.js').LoadingManagerState} */
  get state() {
    return this.#state
  }

  /**
   * Subscribe to loading state changes. Fires immediately with current state.
   * @param {(state: import('./types.js').LoadingManagerState) => void} fn
   * @returns {() => void} unsubscribe
   */
  subscribe(fn) {
    fn(this.#state)
    this.#listeners.add(fn)
    return () => {
      this.#listeners.delete(fn)
    }
  }

  /** Reset all entries — call on logout. */
  reset() {
    this.#entries.clear()
    this.#notify()
  }

  /**
   * Recompute state now; tell subscribers about it on a microtask.
   *
   * The delivery is deferred because operations start from wherever the work
   * starts, and for a query that is during a component's render — `createQuery`
   * runs while the page is being built. A synchronous callback from there lands
   * a `$state` write inside the render pass, which Svelte rejects outright
   * (`state_unsafe_mutation`). The throw escapes mid-flush, so the route's
   * `{#key}` block never finishes swapping and the old page is left mounted
   * beneath the new one — every navigation stacking another dead screen.
   *
   * `state` itself stays synchronous: callers that read it after start/finish
   * must see the truth immediately. Only the fan-out waits, which also collapses
   * a start + progress + finish burst into a single notification.
   */
  #notify() {
    this.#state = this.#computeState()
    if (this.#notifyQueued) return
    this.#notifyQueued = true
    queueMicrotask(() => {
      this.#notifyQueued = false
      const state = this.#state
      for (const fn of this.#listeners) {
        fn(state)
      }
    })
  }

  #computeState() {
    const entries = Array.from(this.#entries.values())
    const pending = entries.filter(e => e.status === 'pending')
    const routeEntries = pending.filter(e => e.type === 'route')
    const routeProgress = routeEntries.length > 0
      ? routeEntries.reduce((sum, e) => sum + (e.progress || 0), 0) / routeEntries.length
      : 0
    return {
      active: pending.length > 0,
      routeLoading: pending.some(e => e.type === 'route'),
      queryLoading: pending.some(e => e.type === 'query'),
      mutationLoading: pending.some(e => e.type === 'mutation'),
      bootstrapLoading: pending.some(e => e.type === 'bootstrap'),
      entries: pending,
      count: pending.length,
      routeProgress,
    }
  }
}

export const loadingManager = new LoadingManager()
